import {
  NWS_STATE,
  WEATHER_LAT,
  WEATHER_LON,
  WEATHER_LOCATION,
  NWS_RADAR_LOCATION_URL,
} from '../constants/weather';
import { fetchNwsAlertMapFeatures, type NwsAlertMapFeature } from '../services/nwsAlertsGeoService';
import { loadRadarLoopFrames, type RadarLoopFrames } from '../services/weatherRadarLoopService';
import { buildNwsAlertsSvg } from '../utils/nwsAlertMapOverlay';
import { buildIemMosaicTileUrl, IEM_RADAR_CURRENT_LAYER } from '../utils/iemRadarUrls';
import { filterRadarTileImage } from '../utils/radarTileSpeckleFilter';
import { escapeAttr } from '../utils/escapeHtml';
import {
  buildTileGrid,
  DEFAULT_RADAR_RADIUS_STEP_INDEX,
  latLonPixelInTileGrid,
  osmTileUrl,
  RADAR_RADIUS_STEPS_MI,
  radarMapViewForRadiusMiles,
  TILE_SIZE_PX,
  type MapTileCoord,
  type RadarMapView,
} from '../utils/mapTiles';

const FRAME_MS = 500;

/** Latest reflectivity on OSM tiles; optional play for last-hour loop. */
export class WeatherRadarLoop {
  private timer: ReturnType<typeof setInterval> | null = null;
  private playing = false;
  private playBtn: HTMLButtonElement | null = null;
  private timeEl: HTMLElement | null = null;
  private frameIndex = 0;
  private frames: RadarLoopFrames | null = null;
  private alertFeatures: NwsAlertMapFeature[] = [];
  private tiles: MapTileCoord[] = [];
  private radarImgs: HTMLImageElement[] = [];
  private mapView: RadarMapView = radarMapViewForRadiusMiles(
    WEATHER_LAT,
    WEATHER_LON,
    RADAR_RADIUS_STEPS_MI[DEFAULT_RADAR_RADIUS_STEP_INDEX]!
  );
  private radiusStepIndex = DEFAULT_RADAR_RADIUS_STEP_INDEX;
  private viewportEl: HTMLElement | null = null;
  private gridEl: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private readonly wrap: HTMLElement,
    private readonly signal: AbortSignal
  ) {}

  start(): void {
    void this.init();
  }

  stop(): void {
    this.pauseLoop();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private async init(): Promise<void> {
    try {
      const [frames, alerts] = await Promise.all([
        loadRadarLoopFrames(undefined, undefined, this.signal),
        fetchNwsAlertMapFeatures(NWS_STATE, this.signal).catch(() => [] as NwsAlertMapFeature[]),
      ]);
      this.frames = frames;
      this.alertFeatures = alerts;
      if (this.signal.aborted) return;
      this.applyRadiusStep(this.radiusStepIndex);
      this.wrap.removeAttribute('aria-busy');
      this.frameIndex = Math.max(0, this.frames.frames.length - 1);
      this.mountShell();
    } catch {
      if (this.signal.aborted) return;
      this.wrap.innerHTML = `
        <p class="weather-radar-fallback">
          <a href="${escapeAttr(NWS_RADAR_LOCATION_URL)}" target="_blank" rel="noopener noreferrer">Open NWS radar</a>
        </p>`;
    }
  }

  private applyRadiusStep(index: number): void {
    this.radiusStepIndex = Math.max(0, Math.min(RADAR_RADIUS_STEPS_MI.length - 1, index));
    const miles = RADAR_RADIUS_STEPS_MI[this.radiusStepIndex]!;
    this.mapView = radarMapViewForRadiusMiles(WEATHER_LAT, WEATHER_LON, miles);
    this.tiles = buildTileGrid(
      WEATHER_LAT,
      WEATHER_LON,
      this.mapView.zoom,
      this.mapView.radius
    );
  }

  private mountShell(): void {
    const shell = document.createElement('div');
    shell.className = 'weather-radar-map-shell';

    const controls = document.createElement('div');
    controls.className = 'weather-radar-zoom-controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Radar zoom');

    const zoomIn = document.createElement('button');
    zoomIn.type = 'button';
    zoomIn.className = 'weather-radar-zoom-btn';
    zoomIn.title = 'Zoom in';
    zoomIn.setAttribute('aria-label', 'Zoom in');
    zoomIn.textContent = '+';
    zoomIn.addEventListener('click', () => this.changeZoom(-1));

    const zoomOut = document.createElement('button');
    zoomOut.type = 'button';
    zoomOut.className = 'weather-radar-zoom-btn';
    zoomOut.title = 'Zoom out';
    zoomOut.setAttribute('aria-label', 'Zoom out');
    zoomOut.textContent = '−';
    zoomOut.addEventListener('click', () => this.changeZoom(1));

    controls.append(zoomIn, zoomOut);

    const playWrap = document.createElement('div');
    playWrap.className = 'weather-radar-play-control';
    playWrap.setAttribute('role', 'group');
    playWrap.setAttribute('aria-label', 'Radar animation');

    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'weather-radar-play-btn';
    playBtn.setAttribute('aria-label', 'Play radar loop');
    playBtn.textContent = '▶';
    playBtn.addEventListener('click', () => this.togglePlay());
    this.playBtn = playBtn;
    playWrap.append(playBtn);

    const timeEl = document.createElement('div');
    timeEl.className = 'weather-radar-time';
    timeEl.setAttribute('aria-live', 'polite');
    this.timeEl = timeEl;

    const viewport = document.createElement('div');
    viewport.className = 'weather-radar-map-viewport';
    this.viewportEl = viewport;

    shell.append(playWrap, timeEl, controls, viewport);
    this.wrap.replaceChildren(shell);

    this.rebuildTileLayers();
    this.syncFrameTimeLabel();
    this.syncZoomButtons();
    this.syncPlayButton();

    this.resizeObserver = new ResizeObserver(() => this.fitMapScale());
    this.resizeObserver.observe(viewport);
  }

  private syncZoomButtons(): void {
    const shell = this.wrap.querySelector('.weather-radar-map-shell');
    if (!shell) return;
    const buttons = shell.querySelectorAll<HTMLButtonElement>('.weather-radar-zoom-btn');
    const zoomIn = buttons[0];
    const zoomOut = buttons[1];
    if (zoomIn) zoomIn.disabled = this.radiusStepIndex <= 0;
    if (zoomOut) zoomOut.disabled = this.radiusStepIndex >= RADAR_RADIUS_STEPS_MI.length - 1;
  }

  private changeZoom(delta: number): void {
    const next = this.radiusStepIndex + delta;
    if (next < 0 || next >= RADAR_RADIUS_STEPS_MI.length) return;
    this.applyRadiusStep(next);
    this.rebuildTileLayers();
    this.syncZoomButtons();
  }

  private rebuildTileLayers(): void {
    const f = this.frames;
    const viewport = this.viewportEl;
    if (!f || !viewport) return;

    const { zoom, radius } = this.mapView;
    const gridSpan = radius * 2 + 1;
    const gridPx = gridSpan * TILE_SIZE_PX;

    const grid = document.createElement('div');
    grid.className = 'weather-radar-tile-grid';
    grid.style.width = `${gridPx}px`;
    grid.style.height = `${gridPx}px`;
    this.gridEl = grid;

    const baseLayer = document.createElement('div');
    baseLayer.className = 'weather-radar-layer weather-radar-layer-base';
    baseLayer.setAttribute('aria-hidden', 'true');

    const radarLayer = document.createElement('div');
    radarLayer.className = 'weather-radar-layer weather-radar-layer-radar';
    radarLayer.setAttribute('aria-hidden', 'true');

    this.radarImgs = [];
    const frame = f.frames[this.frameIndex] ?? f.frames[0]!;
    const layer = frame.layerId;

    for (const t of this.tiles) {
      const base = document.createElement('img');
      base.className = 'weather-radar-tile';
      base.alt = '';
      base.decoding = 'async';
      base.loading = 'eager';
      base.src = osmTileUrl(zoom, t.x, t.y);
      base.style.left = `${t.px}px`;
      base.style.top = `${t.py}px`;
      baseLayer.appendChild(base);

      const radar = document.createElement('img');
      radar.className = 'weather-radar-tile weather-radar-tile-overlay';
      radar.alt = '';
      radar.decoding = 'async';
      radar.crossOrigin = 'anonymous';
      radar.dataset['tileX'] = String(t.x);
      radar.dataset['tileY'] = String(t.y);
      this.setRadarTileSrc(radar, buildIemMosaicTileUrl(layer, zoom, t.x, t.y), layer);
      radar.onerror = () => {
        if (radar.dataset['fallback'] === '1') {
          radar.style.visibility = 'hidden';
          return;
        }
        radar.dataset['fallback'] = '1';
        this.setRadarTileSrc(
          radar,
          buildIemMosaicTileUrl(IEM_RADAR_CURRENT_LAYER, zoom, t.x, t.y),
          IEM_RADAR_CURRENT_LAYER
        );
      };
      radar.style.left = `${t.px}px`;
      radar.style.top = `${t.py}px`;
      radarLayer.appendChild(radar);
      this.radarImgs.push(radar);
    }

    const alertsLayer = document.createElement('div');
    alertsLayer.className = 'weather-radar-layer weather-radar-layer-alerts';
    if (this.alertFeatures.length > 0) {
      alertsLayer.append(
        buildNwsAlertsSvg(
          this.alertFeatures,
          WEATHER_LAT,
          WEATHER_LON,
          zoom,
          radius,
          gridPx
        )
      );
    }

    const markerLayer = document.createElement('div');
    markerLayer.className = 'weather-radar-layer weather-radar-marker-layer';
    const markerPos = latLonPixelInTileGrid(
      WEATHER_LAT,
      WEATHER_LON,
      zoom,
      radius,
      WEATHER_LAT,
      WEATHER_LON
    );
    const marker = document.createElement('span');
    marker.className = 'weather-radar-marker';
    marker.style.left = `${markerPos.left}px`;
    marker.style.top = `${markerPos.top}px`;
    marker.title = WEATHER_LOCATION;
    marker.setAttribute('aria-label', WEATHER_LOCATION);
    markerLayer.append(marker);

    grid.append(baseLayer, radarLayer, alertsLayer, markerLayer);
    viewport.replaceChildren(grid);
    this.fitMapScale();
  }

  private fitMapScale(): void {
    const viewport = this.viewportEl;
    const grid = this.gridEl;
    if (!viewport || !grid) return;
    const gridPx = (this.mapView.radius * 2 + 1) * TILE_SIZE_PX;
    const w = viewport.clientWidth;
    if (w <= 0) return;
    const scale = w / gridPx;
    grid.style.transform = `translate(-50%, -50%) scale(${scale})`;
  }

  private setRadarTileSrc(img: HTMLImageElement, url: string, layerId: string): void {
    delete img.dataset['fallback'];
    img.dataset['layer'] = layerId;
    img.onload = () => {
      if (img.src.startsWith('data:')) return;
      try {
        const filtered = filterRadarTileImage(img);
        if (filtered) img.src = filtered;
      } catch {
        /* keep upstream tile (e.g. canvas taint) */
      }
    };
    img.style.visibility = '';
    img.src = url;
  }

  private setFrame(index: number): void {
    const f = this.frames!;
    const frame = f.frames[index];
    if (!frame) return;
    const { zoom } = this.mapView;
    for (const img of this.radarImgs) {
      const x = Number(img.dataset['tileX']);
      const y = Number(img.dataset['tileY']);
      this.setRadarTileSrc(
        img,
        buildIemMosaicTileUrl(frame.layerId, zoom, x, y),
        frame.layerId
      );
    }
    this.syncFrameTimeLabel();
  }

  private syncFrameTimeLabel(): void {
    const el = this.timeEl;
    const f = this.frames;
    if (!el || !f) return;
    const frame = f.frames[this.frameIndex];
    if (!frame) {
      el.textContent = '';
      return;
    }
    el.textContent = frame.timeLabel;
    el.setAttribute('aria-label', `Radar frame time: ${frame.timeLabel}`);
  }

  private togglePlay(): void {
    if (this.playing) this.pauseLoop();
    else this.startLoop();
  }

  private syncPlayButton(): void {
    const btn = this.playBtn;
    const f = this.frames;
    if (!btn || !f) return;
    const canPlay = f.frames.length >= 2;
    btn.disabled = !canPlay;
    if (this.playing) {
      btn.textContent = '⏸';
      btn.setAttribute('aria-label', 'Pause radar loop');
    } else {
      btn.textContent = '▶';
      btn.setAttribute('aria-label', 'Play radar loop');
    }
  }

  private pauseLoop(): void {
    this.playing = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const f = this.frames;
    if (f && f.frames.length > 0) {
      this.frameIndex = f.frames.length - 1;
      this.setFrame(this.frameIndex);
    }
    this.syncPlayButton();
  }

  private startLoop(): void {
    const f = this.frames;
    if (!f || f.frames.length < 2) return;

    this.playing = true;
    this.frameIndex = 0;
    this.setFrame(0);
    this.syncPlayButton();

    this.timer = setInterval(() => {
      if (this.signal.aborted) {
        this.stop();
        return;
      }
      this.frameIndex = (this.frameIndex + 1) % f.frames.length;
      this.setFrame(this.frameIndex);
    }, FRAME_MS);
  }
}
